import type { CartPackageImageNodeDisplayModel } from '@shared/models/carts/cartPackageImageNodes';
import type {
  Cartv2PackageDetailsDisplayModel,
  Cartv2PackageDisplayModel,
  Cartv2PackageItemDisplayModel,
} from '@shared/models/carts/cartPackages';
import type { CartPackageTextNodeDisplayModel } from '@shared/models/carts/cartPackageTextNotes';
import type { SubjectFieldCreateModel, SubjectFieldDisplayModel } from '@shared/models/subjects/subjectFields';
import { DIGITAL_DOWNLOAD_CODES, isV3MultiImageDigitalDownload } from '@shared/models/products/productLookup';
import { isCanonicalTextFieldName, isV3EditableTextNode } from '@shared/services/packageNodes.service';

// Multi-image DD text node names
const SELECTED_IMAGE_IDS_NODE_NAME = 'SelectedImageIds';

/**
 * Simple hash function for detecting value changes.
 * Not cryptographic, just needs to be consistent and fast.
 * Returns an 8-character hex string (unsigned 32-bit).
 */
export const simpleHash = (str: string): string => {
  if (!str) return '';
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash = hash & hash; // Convert to 32bit integer
  }
  // Use >>> 0 to convert to unsigned 32-bit integer (no negative sign)
  // Pad to 8 characters for consistent length
  return (hash >>> 0).toString(16).padStart(8, '0');
};

const obscureValue = (value: string | undefined) => {
  if (!value) return '';
  const firstValue = value.replace(/(.{2})(.*)(?=@)/, (gp1, gp2, gp3) => {
    for (let i = 0; i < gp3.length; i++) {
      gp2 += '*';
    }
    return gp2;
  });

  if (firstValue !== value) {
    return firstValue;
  }
  const totalValueLength = value.length > 5 ? 5 : 0;
  return value.replace(value.substring(value.length, totalValueLength), '*****');
};

/**
 * Whether a value is a masked display value produced by {@link obscureValue} rather than something a
 * user typed. Used to detect (and re-sync) nodes whose mask leaked into storage — no real email or
 * phone number contains a run of asterisks.
 */
export const isObscuredValue = (value: string | undefined | null): boolean => /\*{2,}/.test(value ?? '');

/**
 * The value to persist for a text node: the real value behind the mask when the user left an obscured
 * field alone, otherwise whatever is in the field.
 *
 * The real value is read off the node itself (`realValue`) so it survives the spreads and JSON clones
 * the cart flows do — a lookup keyed by object identity silently missed after the first copy and saved
 * the mask, which then reached the lab as the customer's delivery address.
 */
export const saveableTextNodeValue = (node: CartPackageTextNodeDisplayModel) => {
  // For obscured nodes, check if user modified by comparing displayed obscured value
  if (node.obscure) {
    const realValue = node.realValue;
    if (realValue && node.value === obscureValue(realValue)) {
      // User didn't change the obscured value, return the real value
      return realValue;
    }
  }
  return node.value;
};

export const populateTextNodeValue = (textNode: CartPackageTextNodeDisplayModel, value: string) => {
  if (textNode.obscure) {
    textNode.value = obscureValue(value);
    textNode.realValue = value;
    // Obscured nodes compare against the untrimmed real value, so hash it untrimmed.
    textNode.lastSyncedValueHash = simpleHash(value);
  } else {
    textNode.value = value;
    textNode.realValue = undefined;
    // Hash the trimmed value to match the trimmed comparison in fillTextNode — keeps a
    // transient trailing space from falsely flagging the node as user-modified.
    textNode.lastSyncedValueHash = simpleHash(value.trim());
  }
};

export const getImageNodes = (item: Cartv2PackageItemDisplayModel): CartPackageImageNodeDisplayModel[] =>
  item.imageNodes;
export const getTextNodes = (item: Cartv2PackageItemDisplayModel): CartPackageTextNodeDisplayModel[] => item.textNodes;
export const getPackageTextNodes = (pkg: Cartv2PackageDisplayModel) =>
  pkg.cartPackageItems.map((i) => getTextNodes(i)).flat();
export const getPackageImageNodes = (pkg: Cartv2PackageDisplayModel) =>
  pkg.cartPackageItems.map((i) => getImageNodes(i)).flat();

export const isTextNodeComplete = (textNode: CartPackageTextNodeDisplayModel): boolean => {
  return !!textNode.value?.trim();
};

/**
 * Canonical-name / alias predicates now live in the shared node service so that every consumer- and
 * studio-facing text-node surface applies the SAME rule (`MLivePreviewModal`, `LivePreviewItemCard`,
 * `PackageItemEditor`, and completeness below). Re-exported here for existing importers.
 *
 * @see https://github.com/Millers-IT/tickets — fix/display-proper-text-nodes-for-v3-hosting-pre-order
 */
export {
  isCanonicalTextFieldName,
  hasTextNodeAlias,
  isV3EditableTextNode,
} from '@shared/services/packageNodes.service';

/**
 * The text nodes a consumer card should render as inputs, deduped by name (preferring a node that
 * already holds a value): canonical names only (`Year` / `Text<n>`), plus the alias requirement on V3.
 *
 * Extracted from `LivePreviewItemCard` so every consumer surface that collects text — the preview
 * card AND `MultiImageDownloadItemCard`, which has no preview to click into — surfaces exactly the
 * same set the Add To Cart gate validates. When the two disagreed, a multi-image download's
 * `Text1=Email` node was validated but never rendered, leaving the consumer with an unclearable
 * "Please enter a valid email address" error.
 */
export const getEditableConsumerTextNodes = (
  nodes: CartPackageTextNodeDisplayModel[],
  isV3: boolean,
): CartPackageTextNodeDisplayModel[] => {
  const seen: Record<string, CartPackageTextNodeDisplayModel> = {};
  const order: string[] = [];
  for (const node of nodes ?? []) {
    const name = node?.name;
    if (!name) continue;
    // Canonical check applies to legacy too, so it stays unconditional. Only the alias requirement
    // is V3-gated, and it goes through isV3EditableTextNode so the `Year` exemption lives in one place.
    if (!isCanonicalTextFieldName(name)) continue;
    if (isV3 && !isV3EditableTextNode(node)) continue;
    if (!seen[name]) {
      seen[name] = node;
      order.push(name);
    } else if (!(seen[name].value || '').trim() && (node.value || '').trim()) {
      seen[name] = node;
    }
  }
  return order.map((name) => seen[name]);
};

/**
 * Marker a package item stores in place of "Email" when the product template's TemplInfo assigned the
 * node to the delivery email. It means "this is the delivery email and the studio may not reassign it"
 * — see `BasePackageItemTextNodeDisplayModel.EmailRequiredDataSource`.
 *
 * It is a marker, not a field name: anything reading it as one (labels, subject-data lookups) must go
 * through {@link normalizeDataSource} first. Email *detection* needs no special handling — the marker
 * starts with "email", so {@link isEmailFieldName} already matches it.
 */
export const EMAIL_REQUIRED_DATA_SOURCE = 'Email-Required';

/** The marker resolved back to the field name it stands for; any other value passes through. */
export const normalizeDataSource = <T extends string | undefined | null>(dataSource: T): T | string =>
  dataSource?.trim().toLowerCase() === EMAIL_REQUIRED_DATA_SOURCE.toLowerCase() ? 'Email' : dataSource;

/** Friendly field label — prefer the template's alias (e.g. "Email", "Name", "Team", "Jersey"). */
export const textNodeDisplayLabel = (node: CartPackageTextNodeDisplayModel): string => {
  if (node.altDataSource) return normalizeDataSource(node.altDataSource) as string;
  const name = node.name;
  if (name === 'Year') return 'Year';
  if (name === 'Title') return 'Title';
  const match = name.match(/^Text(\d+)$/i);
  if (match) return `Text Line ${match[1]}`;
  if (name.toLowerCase() === 'text') return 'Text';
  return name;
};

export const textNodePlaceholder = (node: CartPackageTextNodeDisplayModel): string =>
  node.altDataSource ? `Enter ${(normalizeDataSource(node.altDataSource) as string).toLowerCase()}...` : '';

/**
 * Write `value` onto every node sharing the edited node's name OR its alias, returning a new array.
 * Multi-page items repeat the same field across pages, so an edit has to land on all of them.
 */
export const applyValueToMatchingTextNodes = (
  nodes: CartPackageTextNodeDisplayModel[],
  nodeName: string,
  value: string,
): CartPackageTextNodeDisplayModel[] => {
  const sourceAlt = (nodes ?? []).find((node) => node.name === nodeName)?.altDataSource;
  return (nodes ?? []).map((node) =>
    node.name === nodeName || (sourceAlt && node.altDataSource === sourceAlt) ? { ...node, value } : node,
  );
};

/**
 * Whether a resolved field name (dataSource / altDataSource / node name) is an email field.
 * Matches the same `email*` prefix the obscuring rule uses, so `Email`, `Email Address` and
 * `EmailParent` are all treated as the customer's delivery email.
 */
export const isEmailFieldName = (name: string | undefined | null): boolean =>
  !!name && name.trim().toLowerCase().startsWith('email');

export const isDigitalDownloadProduct = (item: Cartv2PackageItemDisplayModel): boolean => {
  return DIGITAL_DOWNLOAD_CODES.some((c) => c == item.productCode);
};

/**
 * Checks if a cart package item is a multi-image digital download that requires image selection.
 */
export const isMultiImageDigitalDownload = (item: Cartv2PackageItemDisplayModel): boolean => {
  return isV3MultiImageDigitalDownload(item.productCode);
};

/**
 * Checks if a multi-image digital download item has images selected.
 * Returns true if not a multi-image DD, or if images have been selected.
 */
export const isMultiImageSelectionComplete = (item: Cartv2PackageItemDisplayModel): boolean => {
  if (!isMultiImageDigitalDownload(item)) return true;

  // Must have the SelectedImageIds node with a non-empty value
  const selectedIdsNode = item.textNodes.find((n) => n.name === SELECTED_IMAGE_IDS_NODE_NAME);
  return !!selectedIdsNode?.value?.trim();
};

export const areTextNodesComplete = (item: Cartv2PackageItemDisplayModel, isV3: boolean): boolean => {
  if (isDigitalDownloadProduct(item)) return true; // Digital download items from RS+ are always complete
  if (isMultiImageDigitalDownload(item)) return true; // Multi-image DD items don't have editable text nodes
  const textNodes = getTextNodes(item);
  // Completeness must mirror what the consumer can actually see and edit — a hidden node
  // must never block Add To Cart. V3 (RSPO): subject data is on rails, so only canonical
  // fields (`Year` / `Text<n>`) that also have an alias (data-field binding) are surfaced and
  // gate completeness — an unbound `Text<n>` with no alias is hidden. Legacy: node names and
  // aliases are free-form (e.g. `Name`, `Grade`, `Custom1` with no dataSource), so there is
  // no reliable field-vs-junk signal — every text node is surfaced and gates completeness,
  // matching long-standing legacy behavior.
  const relevantNodes = isV3 ? textNodes.filter(isV3EditableTextNode) : textNodes;
  return relevantNodes.every(isTextNodeComplete);
};

/**
 * The subject-data field a text node maps to (`dataSource` → `altDataSource` → `name`), lowercased.
 *
 * The single resolver for this rule — {@link fillTextNode} (add-to-cart) and the Entry build
 * screen's subject-data sync watcher both call it, so a node fills and re-syncs by the same key.
 * They used to resolve it separately and drifted: the watcher read the `Email-Required` marker as a
 * literal field name, matched no subject field, and (having no blank guard) wiped the delivery
 * email off already-built items whenever any other subject field changed.
 *
 * `altDataSource` goes through {@link normalizeDataSource} because it can hold that marker, which
 * stands for the `Email` field rather than naming one. `||` rather than `??` so an empty-string
 * `dataSource` — the mapper passes `PackageItemTextNode.DataSource` through raw — falls through to
 * the alias instead of short-circuiting to nothing.
 */
export const resolvedFieldName = (node: CartPackageTextNodeDisplayModel): string =>
  node.dataSource?.toLowerCase() ||
  (normalizeDataSource(node.altDataSource) as string | undefined)?.toLowerCase() ||
  node.name?.toLowerCase() ||
  '';

/**
 * The email fields an item actually shows the consumer. Restricted to what is on screen for the same
 * reason completeness is (see `areTextNodesComplete`): a node the consumer can't see must never block
 * Add To Cart.
 *
 * **V3 (RSPO) only.** Collecting a delivery email from the consumer is an RSPO behavior: those
 * templates name the node `Text<n>` and bind it to Email through TemplInfo, which is what
 * `isV3EditableTextNode` detects and what the ordering UI renders a field for. Legacy templates carry
 * free-form nodes (e.g. one literally named `Email`, no dataSource) that no legacy surface renders —
 * `PackageItemEditor` shows only nodes the server marks `editable` (`^Text\d{1,2}$|^Year$`, non-static)
 * and hides the whole panel for whole-download product codes. Treating those as a required delivery
 * address deadlocked the add: blocked on an email with no input on screen to satisfy it. Legacy keeps
 * its pre-existing behavior instead — the node is auto-filled from the subject's data field
 * (see {@link consumerTextNodeFillOptions}) and nothing gates checkout on it.
 */
export const getConsumerEmailTextNodes = (
  item: Cartv2PackageItemDisplayModel,
  isV3: boolean,
): CartPackageTextNodeDisplayModel[] => {
  if (!isV3) return [];
  return getTextNodes(item).filter((node) => isEmailFieldName(resolvedFieldName(node)) && isV3EditableTextNode(node));
};

/**
 * Deliberately loose shape check: one `@` separating a non-empty local part from a domain that
 * contains a `.` with something after it, and no whitespace anywhere. Enough to catch the real
 * mistakes (missing `@`, missing domain suffix, a stray name typed into the field) without rejecting
 * valid-but-unusual addresses the way a stricter RFC pattern would.
 *
 * Note this also accepts the obscured form the studio-side flows produce (`cu****@example.com`), so
 * an auto-filled value can never read as invalid.
 */
const EMAIL_SHAPE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** Whether a text node value looks like an email address (has an `@` and a `.` after it). */
export const isValidEmailValue = (value: string | undefined | null): boolean => EMAIL_SHAPE.test((value ?? '').trim());

/**
 * Whether every email field the consumer is shown on this item holds a usable email address.
 * Always `true` on legacy jobs, which collect no delivery email — see `getConsumerEmailTextNodes`.
 *
 * Deliberately separate from `areTextNodesComplete`, which short-circuits to `true` for digital
 * download products — those are exactly the items that collect a delivery email, and since the email
 * is no longer pre-filled from subject data on V3 (see `fillTextNode`) an unanswered or mistyped
 * field would otherwise reach the lab unusable.
 */
export const areEmailTextNodesValid = (item: Cartv2PackageItemDisplayModel, isV3: boolean): boolean =>
  getConsumerEmailTextNodes(item, isV3).every((node) => isValidEmailValue(node.value));

/**
 * Display names (deduped) of the items whose consumer-visible email field is blank or malformed —
 * what the Add To Cart gate reports. Kept here rather than as a store getter so the gate is a pure
 * function of the items it is handed.
 */
export const getItemsNeedingValidEmail = (items: Cartv2PackageItemDisplayModel[], isV3: boolean): string[] => [
  ...new Set(items.filter((item) => !areEmailTextNodesValid(item, isV3)).map((item) => item.displayName || item.name)),
];

/** Error copy shown when the consumer tries to add to cart with an email field blank or malformed. */
export const emailRequiredMessage = (itemNames: string[]): string =>
  itemNames.length
    ? `Please enter a valid email address for: ${itemNames.join(', ')}.`
    : 'Please enter a valid email address for your item(s).';

export const isImageNodeComplete = (imageNode: CartPackageImageNodeDisplayModel): boolean => {
  if (imageNode.subjectImageId) return true;
  if (imageNode.groupImageId) return true;

  return false;
};

export const areImageNodesComplete = (item: Cartv2PackageItemDisplayModel): boolean => {
  const imageNodes = getImageNodes(item);
  return imageNodes.every(isImageNodeComplete);
};

export const isItemComplete = (item: Cartv2PackageItemDisplayModel, isV3: boolean): boolean => {
  return areImageNodesComplete(item) && areTextNodesComplete(item, isV3);
};

/**
 * Image 3 nodes are never defaulted and should always require a user to select an image
 * @param imageNode
 * @returns
 * @see https://github.com/Millers-IT/tickets/issues/4540
 */
export const isImage3Node = (imageNode: CartPackageImageNodeDisplayModel): boolean => {
  return imageNode.name.toLocaleLowerCase().includes('image3');
};

const FIELD_ALIASES = {
  firstname: ['first name', 'firstname'],
  lastname: ['last name', 'lastname'],
  phonenumber: ['phone number', 'phonenumber', 'phone'],
  dateofbirth: ['date of birth', 'dateofbith', 'birthday'],
};

type subjectFields = (SubjectFieldDisplayModel | SubjectFieldCreateModel)[];

export interface TextNodeFillOptions {
  /**
   * V3 (RSPO) consumer ordering: leave email fields for the customer to fill in instead of pre-filling
   * them from the subject's data (imported or entered during build). Off by default so the studio-side
   * build flows — and legacy consumer ordering, which has no field to collect one — keep auto-filling.
   *
   * @see {@link fillTextNode}
   * @see {@link consumerTextNodeFillOptions}
   */
  skipEmailAutoFill?: boolean;
  /**
   * Studio-side flows (Entry build, image matching): fill email/phone fields with the real value
   * instead of a masked one. Masking exists to keep one customer from reading another's contact
   * details in the consumer store; a studio user is already looking at the same subject's data field
   * on the same screen, and — because the field is theirs to correct — a mask they can't see through
   * is what ends up typed over or shipped to the lab.
   *
   * @see {@link fillTextNode}
   */
  revealSensitiveValues?: boolean;
}

/**
 * Fill behavior for a CONSUMER ordering flow on a job of the given type — the single place the
 * "who supplies the delivery email" rule is expressed.
 *
 * V3 (RSPO) prompts the customer for it, so the subject's stored address is not pre-filled. Legacy
 * has no surface that renders an email field (see `getConsumerEmailTextNodes`), so leaving it blank
 * would ship an empty address to the lab — it keeps auto-filling from subject data, exactly as it did
 * before delivery-email collection was introduced.
 *
 * Studio-side build flows (Entry, image matching) do not use this — they pass
 * `revealSensitiveValues` and always auto-fill.
 */
export const consumerTextNodeFillOptions = (isV3: boolean): TextNodeFillOptions => ({
  skipEmailAutoFill: isV3,
});

const fieldFinder = (dataFields: subjectFields, ...names: string[]) =>
  dataFields.find((f) => names.includes(f.name.toLowerCase()));

// Provides a utility function that gets subject data fields based on common field names
export const getSubjectDataField = (dataFields: subjectFields, field: string): SubjectFieldDisplayModel | null => {
  const fieldName = field.toLowerCase();
  const aliasField = Object.values(FIELD_ALIASES).find((a) => a.includes(fieldName));
  const dataField = fieldFinder(dataFields, ...(aliasField || [fieldName])) as SubjectFieldDisplayModel;
  return (
    dataField || {
      id: 0,
      name: field,
      value: '',
      hasOptions: false,
    }
  );
};

/**
 * The subject-record columns that back a data-field name directly. Optional on every caller so
 * surfaces that only have field rows (consumer reloads, older call sites) keep working.
 */
export interface SubjectCoreColumns {
  identifier?: string;
  firstName?: string;
  lastName?: string;
  password?: string;
}

const coreColumnValue = (subject: SubjectCoreColumns | undefined, fieldName: string): string | undefined => {
  if (!subject) return undefined;
  switch (fieldName) {
    case 'firstname':
    case 'first name':
      return subject.firstName;
    case 'lastname':
    case 'last name':
      return subject.lastName;
    case 'id':
    case 'identifier':
    case 'studentid':
    case 'student id':
      return subject.identifier;
    case 'password':
      return subject.password;
    default:
      return undefined;
  }
};

/**
 * The value a text node bound to `field` should carry for this subject.
 *
 * A few data-field names are backed by a column on the subject record, and that column is the
 * truth: it is what gallery sign-in, promotion emails and the backend package-repoint resolver
 * read. `Password` is the clearest case — the column holds the subject's effective access code
 * (random, or derived from whichever field the gallery settings name), while a field row called
 * "Password" is only whatever an import file happened to put in a column of that name, and RSPO
 * never writes one at all. Reading the row first printed an empty or stale code on inserts.
 *
 * Column first, row as fallback, matching `PackageCommandServiceV2.ResolveSubjectValue`. Callers
 * without the subject record (`subject` omitted) fall straight through to the rows.
 */
export const getSubjectDataFieldValue = (
  dataFields: subjectFields,
  field: string,
  subject?: SubjectCoreColumns,
): string => {
  const fieldName = field.toLowerCase();
  if (fieldName === 'name') {
    const firstName = getSubjectDataFieldValue(dataFields, 'firstname', subject);
    const lastName = getSubjectDataFieldValue(dataFields, 'lastname', subject);
    return `${firstName} ${lastName}`.trim();
  }
  const columnValue = coreColumnValue(subject, fieldName);
  if (columnValue) {
    return columnValue;
  }
  return getSubjectDataField(dataFields, field)?.value || '';
};

export const fillTextNode = (
  node: CartPackageTextNodeDisplayModel,
  dataFields: subjectFields,
  options: TextNodeFillOptions = {},
  subject?: SubjectCoreColumns,
): Partial<CartPackageTextNodeDisplayModel> => {
  // resolvedFieldName normalizes the delivery-email marker so it looks up the subject's "Email"
  // field rather than missing entirely — studio-side flows (Entry, image matching) rely on that
  // auto-fill.
  const fieldName = resolvedFieldName(node);
  if (!fieldName) {
    return {
      obscure: false,
      value: node.value || '', // Preserve existing value
      lastSyncedValueHash: '',
    };
  }

  const obscure = !options.revealSensitiveValues && (fieldName.startsWith('email') || fieldName.startsWith('phone'));

  // Consumer ordering: an email collected on an item is where THAT customer's files are delivered,
  // so it is always prompted for and never seeded from the subject's data field — no matter whether
  // that field arrived by import or was typed by the studio during build. Left blank (or whatever
  // the customer has already typed) and not marked auto-filled, so the field stays visible and
  // editable. Studio-side flows (Entry, image matching) pass no options and keep auto-filling.
  if (options.skipEmailAutoFill && isEmailFieldName(fieldName)) {
    return {
      obscure,
      value: node.value || '',
      lastSyncedValueHash: '',
      autoFilled: false,
    };
  }

  // Get the new subject data value for this field
  const newSubjectValue = getSubjectDataFieldValue(dataFields, fieldName, subject);

  // Check if user has modified the value from what was last synced
  // For obscured fields, we need to get the real value to compare
  let currentValueToHash: string;
  if (obscure) {
    // For obscured fields, check if they have the real value stored
    currentValueToHash = node.realValue || node.value?.trim() || '';
  } else {
    currentValueToHash = node.value?.trim() || '';
  }

  const currentValueHash = simpleHash(currentValueToHash);
  const storedHash = node.lastSyncedValueHash?.trim() || '';
  // If there's a stored hash and current doesn't match, user modified (including if they cleared the
  // field). A stored MASK is the exception: it means an earlier session persisted the display value
  // instead of the real one, so re-sync from subject data rather than preserving unusable text.
  const userModified =
    storedHash !== '' && currentValueHash !== storedHash && !(!node.realValue && isObscuredValue(node.value));

  let value: string;
  let lastSyncedValueHash: string;
  let realValue: string | undefined;
  let autoFilled: boolean;

  if (userModified) {
    // User has customized this field - preserve their value
    value = node.value || '';
    lastSyncedValueHash = node.lastSyncedValueHash || '';
    realValue = node.realValue;
    autoFilled = false;
  } else {
    // No user modification - use new subject data
    value = newSubjectValue;
    autoFilled = !!newSubjectValue;

    if (obscure && newSubjectValue) {
      value = obscureValue(newSubjectValue);
      // Keep the real value on the node so the save path can recover it (see saveableTextNodeValue).
      realValue = newSubjectValue;
      // Obscured nodes compare against the untrimmed real value, so hash it untrimmed.
      lastSyncedValueHash = simpleHash(newSubjectValue);
    } else {
      // Hash the trimmed value to match the trimmed comparison above (currentValueToHash).
      // Otherwise a transient trailing space (e.g. while typing "12 45" you pass through
      // "12 ") makes the next comparison's trimmed hash differ from this stored hash,
      // falsely flagging the node as user-modified and permanently blocking auto-sync.
      lastSyncedValueHash = simpleHash(newSubjectValue.trim());
    }
  }

  return {
    obscure,
    value,
    lastSyncedValueHash,
    realValue,
    autoFilled,
  };
};

export const fillAutoFilledTextNode = (
  node: CartPackageTextNodeDisplayModel,
  dataFields: SubjectFieldDisplayModel[],
  hasImportedData: boolean,
  options: TextNodeFillOptions = {},
  subject?: SubjectCoreColumns,
): Partial<CartPackageTextNodeDisplayModel> => {
  const fieldName = node.dataSource?.toLowerCase() || node.altDataSource?.toLowerCase() || node.name?.toLowerCase();

  let autoFilled = false;
  // V3 (RSPO): email is the customer's own delivery address, never subject data — see fillTextNode.
  // Leaving it unflagged keeps the field visible (the `autoFilled` flag hides imported fields from the
  // consumer) when an existing cart package is reloaded for editing. Legacy passes no options and
  // keeps flagging it, so a legacy cart reloads showing the same fields it always has.
  const exemptEmail = !!options.skipEmailAutoFill;
  if (!!fieldName && hasImportedData && !(exemptEmail && isEmailFieldName(fieldName))) {
    // Same column-first resolver as the fill, so a Password node filled from the subject column at
    // add-to-cart is still recognised as imported data (and hidden from the consumer) on reload.
    const value = getSubjectDataFieldValue(dataFields, fieldName, subject);

    autoFilled = !!value;
  }

  return {
    autoFilled,
  };
};
